import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, X, Star } from "lucide-react";
import api from "../../lib/api";

export default function TestimonialsManager({ onChange }) {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", company: "", text: "", rating: 5, order: 0 });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/site"); setList(data.testimonials || []); }
    catch { toast.error("Could not load testimonials"); }
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing("new"); setForm({ name: "", company: "", text: "", rating: 5, order: list.length + 1 }); };
  const startEdit = (t) => { setEditing(t.id); setForm({ name: t.name || "", company: t.company || "", text: t.text || "", rating: t.rating || 5, order: t.order || 0 }); };
  const cancel = () => { setEditing(null); setForm({ name: "", company: "", text: "", rating: 5, order: 0 }); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, order: Number(form.order) || 0, rating: Number(form.rating) || 5 };
      if (editing === "new") await api.post("/admin/testimonials", payload);
      else await api.patch(`/admin/testimonials/${editing}`, payload);
      toast.success("Saved");
      cancel(); load(); onChange && onChange();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete testimonial from "${t.name}"?`)) return;
    try { await api.delete(`/admin/testimonials/${t.id}`); toast.success("Deleted"); load(); onChange && onChange(); }
    catch { toast.error("Delete failed"); }
  };

  if (editing !== null) {
    return (
      <div data-testid="testimonial-editor" className="border border-[#1F3550] bg-[#0D1B2A] p-6 lg:p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-heading font-bold text-xl text-[#F8FAFC]">{editing === "new" ? "New Testimonial" : "Edit Testimonial"}</h3>
          <button onClick={cancel} data-testid="testimonial-cancel" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#94A3B8] hover:text-[#F8FAFC]"><X className="h-4 w-4" /> Cancel</button>
        </div>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Author Name *</Label><Input data-testid="tst-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
          <div><Label>Company / Title *</Label><Input data-testid="tst-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} required /></div>
          <div><Label>Rating (1-5)</Label><Input type="number" min="1" max="5" data-testid="tst-rating" value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} /></div>
          <div><Label>Order</Label><Input type="number" data-testid="tst-order" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} /></div>
          <div className="md:col-span-2"><Label>Quote *</Label><textarea data-testid="tst-text" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} required rows={5} className="mt-2 w-full bg-[#050A0F] border border-[#1F3550] text-[#F8FAFC] focus:border-[#F39C12] focus:ring-1 focus:ring-[#F39C12] px-4 py-2.5 outline-none" /></div>
          <div className="md:col-span-2"><button type="submit" disabled={saving} data-testid="tst-save" className="inline-flex items-center gap-2 bg-[#F39C12] text-[#050A0F] px-6 py-2.5 font-bold uppercase tracking-widest text-xs hover:bg-[#D68910] disabled:opacity-50"><Save className="h-4 w-4" /> {saving ? "Saving..." : "Save"}</button></div>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="testimonials-manager">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading font-bold text-xl text-[#F8FAFC]">{list.length} testimonials</h2>
        <button onClick={startCreate} data-testid="tst-new" className="inline-flex items-center gap-2 bg-[#F39C12] text-[#050A0F] px-5 py-2.5 font-bold uppercase tracking-widest text-xs hover:bg-[#D68910]"><Plus className="h-4 w-4" /> New</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {list.map((t) => (
          <div key={t.id} data-testid={`tst-row-${t.id}`} className="border border-[#1F3550] bg-[#0D1B2A] p-5">
            <div className="flex items-center gap-1 mb-2">{[...Array(t.rating || 5)].map((_, i) => <Star key={i} className="h-3 w-3 fill-[#F39C12] text-[#F39C12]" />)}</div>
            <p className="text-sm text-[#F8FAFC] leading-relaxed">"{t.text}"</p>
            <div className="mt-4 pt-4 border-t border-[#1F3550]">
              <div className="font-heading font-bold text-[#F8FAFC]">{t.name}</div>
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#94A3B8] mt-1">{t.company}</div>
            </div>
            <div className="mt-4 flex gap-2">
              <button onClick={() => startEdit(t)} data-testid={`tst-edit-${t.id}`} className="flex-1 border border-[#1F3550] hover:border-[#F39C12] text-[#F8FAFC] py-1.5 text-[10px] uppercase tracking-widest font-bold transition-colors">Edit</button>
              <button onClick={() => remove(t)} data-testid={`tst-delete-${t.id}`} className="border border-[#1F3550] hover:border-[#EF4444] hover:text-[#EF4444] text-[#94A3B8] px-3 transition-colors"><Trash2 className="h-3 w-3" /></button>
            </div>
          </div>
        ))}
        {list.length === 0 && <div className="col-span-full py-12 text-center text-[#94A3B8] border border-dashed border-[#1F3550]">No testimonials yet.</div>}
      </div>
    </div>
  );
}

function Label({ children }) { return <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#94A3B8]">{children}</label>; }
function Input(props) { return <input {...props} className="mt-2 w-full bg-[#050A0F] border border-[#1F3550] text-[#F8FAFC] focus:border-[#F39C12] focus:ring-1 focus:ring-[#F39C12] px-4 py-2.5 outline-none" />; }
